import { MortgageCalcData } from '@propertypal/shared/src/hooks/useMortgageCalculator';
import { selectUser } from '@propertypal/shared/src/reducers/user';
import trackEvent from '@propertypal/shared/src/services/analytics';
import { getItem, setItem } from '@propertypal/shared/src/services/local.storage';
import { Broker, Property } from '@propertypal/shared/src/types';
import { Profile } from '@propertypal/shared/src/types/websiteUser';
import { generateYears } from '@propertypal/shared/src/utils/dates';
import { formatNumber, parseNumber } from '@propertypal/shared/src/utils/floatToCurrency';
import {
  getDefaultDeposit,
  getDefaultTerm,
} from '@propertypal/shared/src/utils/property/getMortgageCalculatorDefaults';
import { brokerSchema } from '@propertypal/shared/src/validation/schemas/brokerSchema';
import { useFormik } from 'formik';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Loader from 'react-spinners/PulseLoader';
import { useTheme } from 'styled-components';
import Button from '../buttons/Button';
import Checkbox from '../inputs/Checkbox';
import NumberInput from '../inputs/NumberInput';
import SelectInput from '../inputs/SelectInput';
import TextInput from '../inputs/TextInput';
import MortgageCalcError from '../mortgages/calculator/calculator-error/MortgageCalcError';
import { ToastTypes, useToast } from '../toast/ToastContext';
import { Text } from '../typography';
import {
  Container,
  Header,
  HeaderSection,
  Logo,
  Title,
  FormWrapper,
  FieldContainer,
  MortgagePaymentContainer,
  MortgagePayment,
  FormBox,
} from './BrokerCalculator.style';

interface BrokerCalculatorProps {
  property: Property;
  broker: Broker;
  mortgageCalculator: MortgageCalcData;
}

const inputStyle: CSSProperties = {
  flex: 1,
  marginRight: 15,
  marginTop: 15,
};

const ERROR_MESSAGE: ToastTypes = {
  type: 'error',
  message: 'Sorry, something went wrong sending your details. Please try again!',
};

const BrokerCalculator: React.FC<BrokerCalculatorProps> = ({ property, broker, mortgageCalculator }) => {
  const theme = useTheme();
  const user = useSelector(selectUser);
  const { openToast } = useToast();
  const [sent, setSent] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const profile = useRef<Profile>(getItem('formProfile'));
  const { data, loading, errorStatus, getMortgageResult, mortgagePayment, depositPercentage, submitLead, sending } =
    mortgageCalculator;
  const color = broker.primaryColour || theme.primary;
  const secondary = broker.secondaryColour || theme.secondary;
  const headerText = broker.headerText || 'Mortgage Repayment Calculator';

  const formik = useFormik({
    initialValues: {
      paymentTerm: formatNumber(getDefaultTerm(property)),
      propertyValue: formatNumber(property.price?.price),
      deposit: formatNumber(getDefaultDeposit(property)),
      interestRate: broker.defaultRate,
      fromEmail: user.details.email || profile.current?.email || '',
      fromTelephone: user.details.phoneNumber || profile.current?.phoneNumber || '',
      fromName: user.details.name || profile.current?.name || '',
    },
    validationSchema: brokerSchema,
    onSubmit: async (values) => {
      if (rememberMe) {
        setItem('formProfile', {
          name: values.fromName,
          email: values.fromEmail,
          phoneNumber: values.fromTelephone,
        });
      }

      trackEvent('PDP Calculator', 'Enquiry', 'Broker Calculator', String(property.id));

      const res = await submitLead(broker, values);

      if (res) setSent(true);
      else openToast(ERROR_MESSAGE);
    },
  });

  useEffect(() => {
    getMortgageResult({
      paymentTerm: parseInt(formik.values.paymentTerm, 10),
      deposit: parseNumber(formik.values.deposit),
      propertyValue: parseNumber(formik.values.propertyValue),
    });
  }, [formik.values.paymentTerm, formik.values.deposit, formik.values.propertyValue]);

  useEffect(() => {
    const initialTerm = getItem('defaultTerm');
    const initialDeposit = getItem('defaultDeposit');

    if (initialTerm) formik.setFieldValue('paymentTerm', initialTerm);
    if (initialDeposit) formik.setFieldValue('deposit', initialDeposit);
  }, []);

  return (
    <Container data-testid="broker-calculator">
      <form data-testid="broker-calc-form" onSubmit={formik.handleSubmit}>
        <FormBox>
          <Header>
            <HeaderSection>
              {broker.defaultLogo && <Logo src={broker.defaultLogo} alt={`${broker.name} Broker Calculator`} />}
            </HeaderSection>

            <Title hasLogo={Boolean(broker.defaultLogo)} color={color} data-testid="header-text">
              {headerText}
            </Title>
          </Header>

          <FormWrapper>
            <FieldContainer>
              <NumberInput
                containerStyle={inputStyle}
                prefix="£"
                label="Property Value"
                name="propertyValue"
                onValueChange={formik.handleChange('propertyValue')}
                value={formik.values.propertyValue}
                error={formik.errors.propertyValue}
                testID="propertyValue"
              />

              <NumberInput
                containerStyle={inputStyle}
                prefix="£"
                label={`Deposit (${depositPercentage}%)`}
                name="depositInput"
                onValueChange={(value) => {
                  formik.handleChange('deposit')(value);
                  setItem('defaultDeposit', value);
                }}
                value={formik.values.deposit}
                error={formik.errors.deposit}
                testID="depositInput"
              />

              <SelectInput
                containerStyle={inputStyle}
                labelStyle={{ fontSize: 16, color: theme.textDark }}
                label="Payment Term"
                ariaLabel="Payment Term"
                id="paymentTerm"
                options={generateYears(40, true)}
                value={formik.values.paymentTerm}
                onChange={(value) => {
                  formik.handleChange('paymentTerm')(value);
                  setItem('defaultTerm', value);
                }}
              />

              <TextInput
                containerStyle={inputStyle}
                disabled
                label="Interest Rate"
                name="interestRate"
                value={`${data?.product?.initialRate || 0}%`}
              />
            </FieldContainer>

            <MortgagePaymentContainer>
              <MortgagePayment>
                <Text fontWeight="600" mt={15}>
                  Monthly Payments
                </Text>

                {!loading && (
                  <Text
                    color={secondary}
                    fontWeight="800"
                    fontSize={44}
                    align="center"
                    data-testid="mortgage-repayments"
                  >
                    {mortgagePayment || '-'}
                  </Text>
                )}

                {loading && (
                  <div style={{ marginTop: 25 }}>
                    <Loader color={theme.backgroundLighter} />
                  </div>
                )}
              </MortgagePayment>
            </MortgagePaymentContainer>
          </FormWrapper>

          {errorStatus && <MortgageCalcError testID="broker-calc-error" errors={errorStatus} broker />}

          <Text fontSize={11.5} mt={15}>
            The figures provided by this calculator are for illustrative purposes only. It does not include all of the
            details you may need to consider when calculating mortgage affordability. The accuracy and applicability in
            regards to your individual circumstances cannot be guaranteed. You are encouraged to seek personalised
            advice from qualified professionals regarding all personal finance issues.
          </Text>
        </FormBox>

        <FormBox style={{ backgroundColor: theme.backgroundLightest }}>
          <Title formTitle color={color}>
            Want to check your mortgage eligibility?
          </Title>

          {!sent ? (
            <FormWrapper>
              <FieldContainer>
                <div style={{ flex: 1 }}>
                  <FieldContainer>
                    <TextInput
                      containerStyle={inputStyle}
                      label="Your name"
                      name="fromName"
                      placeholder="Your name"
                      onValueChange={formik.handleChange('fromName')}
                      value={formik.values.fromName}
                      error={formik.touched.fromName && formik.errors.fromName}
                      testID="fromName"
                    />

                    <TextInput
                      containerStyle={inputStyle}
                      label="Your Phone number"
                      name="fromTelephone"
                      placeholder="Your Phone number"
                      onValueChange={formik.handleChange('fromTelephone')}
                      value={formik.values.fromTelephone}
                      error={formik.touched.fromTelephone && formik.errors.fromTelephone}
                      testID="fromTelephone"
                    />

                    <TextInput
                      containerStyle={inputStyle}
                      label="Your email"
                      name="fromEmail"
                      placeholder="Your email"
                      onValueChange={formik.handleChange('fromEmail')}
                      value={formik.values.fromEmail}
                      error={formik.touched.fromEmail && formik.errors.fromEmail}
                      testID="fromEmail"
                    />
                  </FieldContainer>

                  <Checkbox
                    containerStyle={{ marginTop: 15 }}
                    label="Remember my details"
                    name="rememberMe"
                    value={rememberMe}
                    onValueChange={setRememberMe}
                  />
                </div>

                <Button
                  type="submit"
                  loading={sending}
                  disabled={sending}
                  testID="submitButton"
                  color={secondary}
                  hoverColor={color}
                >
                  Submit Details
                </Button>
              </FieldContainer>
            </FormWrapper>
          ) : (
            <>
              <Text
                data-testid="successText"
                fontSize={14}
                fontWeight="bold"
                align="center"
                style={{ marginTop: '60px' }}
              >
                Thank you for your enquiry.
              </Text>
              <Text fontSize={14} fontWeight="bold" align="center" style={{ marginBottom: '60px' }}>
                A member of our team will be in touch with you shortly.
              </Text>
            </>
          )}

          {broker.complianceText && (
            <Text fontSize={11.5} mt={50}>
              {broker.complianceText}
            </Text>
          )}
        </FormBox>
      </form>
    </Container>
  );
};

export default BrokerCalculator;
